import { useCallback, useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { StrategicObjectives } from './StrategicObjectives';
import { RiskOverviewPanel } from './RiskOverviewPanel';
import { PathGuidancePanel } from './PathGuidancePanel';
import { MilestoneDependencyGraph } from './MilestoneDependencyGraph';
import { ActivationStatusCard } from './ActivationStatusCard';
import { EngagementObjectivesDialog } from './EngagementObjectivesDialog';
import { CommunicationMetrics } from './messaging';
import { projectId } from '../utils/supabase/info';

/**
 * EngagementOverview — the firm's view of one engagement on the client page's Overview tab
 * (RESTORE_PLAN R2.6). It carries what the Engagement Plan, the Engagement Command Center and
 * its Activation tab showed: the health snapshot (overall, timeline, scope, risk), activation
 * and onboarding status, strategic objectives with Refine, the risk overview, path guidance,
 * the milestone dependency graph with risk tooltips, and communication health.
 */

export type HealthDot = 'green' | 'yellow' | 'red' | 'neutral';

export interface EngagementBrief {
  engagementId?: string | null;
  status?: string;
  health?: string;
  nextStep?: string;
  snapshot?: { overall: HealthDot; timeline: HealthDot; scope: HealthDot; risk: HealthDot };
}

const DOT: Record<HealthDot, { tone: string; text: string }> = {
  green: { tone: 'bg-emerald-500', text: 'On track' },
  yellow: { tone: 'bg-amber-400', text: 'Watch' },
  red: { tone: 'bg-red-500', text: 'Needs attention' },
  neutral: { tone: 'bg-slate-300', text: 'Not set' },
};

const BASE = `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd`;

interface EngagementOverviewProps {
  companyId: string;
  companyName: string;
  /** The focused engagement (the page's switcher); the routes fall back to theirs when absent. */
  engagementId?: string | null;
  accessToken: string;
  currentUser: any;
  /** Path guidance's "Start this step": the engine's category key, for the page to open. */
  onStartStep?: (hint: string) => void;
  /** Opens the SOWs tab (activation's first step). */
  onOpenSows?: () => void;
}

export function EngagementOverview({ companyId, companyName, engagementId, accessToken, currentUser, onStartStep, onOpenSows }: EngagementOverviewProps) {
  const [plan, setPlan] = useState<{ recommendations: any; context: any } | null>(null);
  const [brief, setBrief] = useState<EngagementBrief | null>(null);
  const [loading, setLoading] = useState(true);
  const [refining, setRefining] = useState(false);

  const query = engagementId ? `?engagementId=${encodeURIComponent(engagementId)}` : '';

  const loadPlan = useCallback(async () => {
    try {
      const res = await fetch(`${BASE}/client/recommendations/${encodeURIComponent(companyId)}${query}`, { headers: { Authorization: `Bearer ${accessToken}` } });
      setPlan(res.ok ? await res.json() : null);
    } catch {
      setPlan(null);
    }
  }, [companyId, query, accessToken]);

  const loadBrief = useCallback(async () => {
    try {
      const res = await fetch(`${BASE}/consultant/engagement-brief/${encodeURIComponent(companyId)}${query}`, { headers: { Authorization: `Bearer ${accessToken}` } });
      setBrief(res.ok ? await res.json() : null);
    } catch {
      setBrief(null);
    }
  }, [companyId, query, accessToken]);

  useEffect(() => {
    setLoading(true);
    void Promise.all([loadPlan(), loadBrief()]).finally(() => setLoading(false));
  }, [loadPlan, loadBrief]);

  const context = plan?.context ?? {};
  const recommendations = plan?.recommendations ?? {};
  const planEngagementId: string | null = engagementId || context.engagementId || brief?.engagementId || null;
  const completed = Array.isArray(context.completedMilestones) ? context.completedMilestones.length : 0;
  const contracted = Array.isArray(context.scopeDivergence?.sowScope) ? context.scopeDivergence.sowScope.length : null;
  const snapshot = brief?.snapshot ?? { overall: 'neutral', timeline: 'neutral', scope: 'neutral', risk: 'neutral' };

  return (
    <div className="space-y-6" data-testid="engagement-overview">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card data-testid="health-snapshot">
          <CardHeader>
            <CardTitle>Engagement health</CardTitle>
            <CardDescription>{brief?.nextStep ? `Next: ${brief.nextStep}` : 'Timeline, scope and risk from the engagement\'s items'}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <dl className="grid grid-cols-2 gap-4">
              {([['Overall', snapshot.overall], ['Timeline', snapshot.timeline], ['Scope', snapshot.scope], ['Risk', snapshot.risk]] as Array<[string, HealthDot]>).map(([label, dot]) => (
                <div key={label} className="flex items-center gap-3" data-testid={`health-${label.toLowerCase()}`} data-dot={dot}>
                  <span className={`size-2.5 shrink-0 rounded-full ${DOT[dot].tone}`} aria-hidden="true" />
                  <div>
                    <dt className="text-muted-foreground">{label}</dt>
                    <dd>{DOT[dot].text}</dd>
                  </div>
                </div>
              ))}
            </dl>
            <p className="border-t pt-3 text-muted-foreground" data-testid="engagement-status-line">
              {completed > 0 ? `${completed} ${completed === 1 ? 'milestone' : 'milestones'} done` : 'Initial setup'}
              {contracted !== null ? ` · contracted scope: ${contracted} ${contracted === 1 ? 'deliverable' : 'deliverables'}` : ''}
            </p>
          </CardContent>
        </Card>

        <ActivationStatusCard companyId={companyId} engagementId={engagementId} accessToken={accessToken} onOpenSows={onOpenSows} />
      </div>

      {loading && !plan ? (
        <p className="flex items-center gap-2 text-muted-foreground"><Loader2 className="size-4 animate-spin" aria-hidden="true" /> Loading the engagement plan…</p>
      ) : (
        <>
          <StrategicObjectives
            hlos={context.hlos || []}
            structuredHLOs={context.structuredHLOs || []}
            painPoints={context.painPoints || []}
            engagementId={planEngagementId ?? undefined}
            hloAlignment={context.hloAlignment ?? null}
            onRefineObjectives={planEngagementId ? () => setRefining(true) : undefined}
          />

          {recommendations.risks && <RiskOverviewPanel risks={recommendations.risks} userRole="consultant" />}

          {plan && (
            <PathGuidancePanel
              companyId={companyId}
              accessToken={accessToken}
              initialData={plan}
              onActionClick={onStartStep ? (item: any) => onStartStep(String(item?.category ?? item?.id ?? '')) : undefined}
              onViewPlan={() => document.getElementById('milestone-plan')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
            />
          )}

          <MilestoneDependencyGraph graph={recommendations.graph ?? []} showRisks />
        </>
      )}

      {planEngagementId && (
        <CommunicationMetrics engagementId={planEngagementId} engagementName={companyName} accessToken={accessToken} showToggle />
      )}

      {planEngagementId && (
        <EngagementObjectivesDialog
          open={refining}
          onClose={() => setRefining(false)}
          engagementId={planEngagementId}
          accessToken={accessToken}
          currentUser={currentUser}
          onChanged={() => { void loadPlan(); }}
        />
      )}
    </div>
  );
}
